import { useMemo, type ReactNode } from 'react';

// Context
import { ServiceContext } from './service.context';

// HTTP
import { HttpKyClient } from '../http/ky.adapter';

// DB
import { AppDB } from '@/shared/lib/db';

// Stores
import { GetStoresUseCase } from '@/modules/stores/services/get-stores.usecase';
import { SearchMerchantsUseCase } from '@/modules/stores/services/search-merchants.usecase';
import { GetMerchantDetailsUseCase } from '@/modules/stores/services/get-merchant-details.usecase';
import { GetStoreReviewsUseCase } from '@/modules/stores/services/get-store-reviews.usecase';
import { GetPlaceUseCase } from '@/modules/location/services/use-cases/get-place.usecase';

// Location
import { AddUserAddressUseCase } from '@/modules/location/services/use-cases/add-user-address.usecase';
import { EditUserAddressUseCase } from '@/modules/location/services/use-cases/edit-user-address.usecase';
import { DeleteUserAddressUseCase } from '@/modules/location/services/use-cases/delete-user-address.usecase';
import { ListUserAddressesUseCase } from '@/modules/location/services/use-cases/list-user-addresses.usecase';
import { GetSelectedAddressUseCase } from '@/modules/location/services/use-cases/get-selected-address.usecase';
import { SetSelectedAddressUseCase } from '@/modules/location/services/use-cases/set-selected-address.usecase';
import { ConvertCoordinatesToAddressUseCase } from '@/modules/location/services/convert-coordinates-to-address.usecase';
import { LookupCepUseCase } from '@/modules/location/services/use-cases/lookup-cep.usecase';

// Stores
import { GetStoreItemsUseCase } from '@/modules/stores/services/get-store-items.usecase';

// Orders
import { ListOrdersUseCase } from '@/modules/orders/services/list-orders.usecase';
import { GetDeliveryTaxUseCase } from '@/modules/orders/services/get-delivery-tax.usecase';
import { MakeOrderUsecase } from '@/modules/orders/services/make-order.usecase';
import { GetPaymentWaysUseCase } from '@/modules/orders/services/get-payment-ways.usecase';
import { GetOrderUseCase } from '@/modules/orders/services/get-order.usecase';
import { GetOrderChatTokenUseCase } from '@/modules/orders/services/get-order-chat-token.usecase';
import { ChangePaymentMethodUseCase } from '@/modules/orders/services/change-payment-method.usecase';
import { RedeemCupomUseCase } from '@/modules/orders/services/redeem-cupom.usecase';
import { ListAvailableCouponsUseCase } from '@/modules/orders/services/list-available-coupons.usecase';
import { ReorderUseCase } from '@/modules/orders/services/reorder.usecase';
import { CheckPendingOrdersService } from '@/modules/orders/services/check-pending-orders.service';
import { GetAvailableScheduleUseCase } from '@/modules/orders/services/get-avaible-schedule.usecase';
import { GetOrderStatusHistoryUseCase } from '@/modules/orders/services/get-order-status-history.usecase';
import { RateOrderUseCase } from '@/modules/orders/services/rate-order.usecase';
import { VerifyPaymentUseCase } from '@/modules/orders/services/verify-payment.usecase';

// Cart
import { GetItemUseCase } from '@/modules/cart/services/get-item.usecase';
import { EditItemUseCase } from '@/modules/cart/services/edit-item.usecase';
import { AddItemUseCase } from '@/modules/cart/services/add-item.usecase';
import { RemoveItemUseCase } from '@/modules/cart/services/remove-item.usecase';
import { IncrementQuantityUseCase } from '@/modules/cart/services/increment-quantity.usecase';
import { DecrementQuantityUseCase } from '@/modules/cart/services/decrement-quantity.usecase';
import { ListItemsUseCase } from '@/modules/cart/services/list-items.usecase';
import { ClearCartUseCase } from '@/modules/cart/services/clear-cart.usecase';
import { CheckCartAvailabilityUseCase } from '@/modules/cart/services/check-cart-availability.usecase';

// Account
import { GetProfileUseCase } from '@/modules/account/services/get-profile.usecase';
import { UpdateProfileUseCase } from '@/modules/account/services/update-profile.usecase';
import {
  GetProfileCompletionUseCase,
  RequestEmailConfirmationUseCase,
} from '@/modules/profile-completion/services/profile-completion.usecase';

// Clube pratiGO (PRAT-27)
import { GetClubSummaryUseCase } from '@/modules/club/services/get-club-summary.usecase';
import { GetClubCatalogUseCase } from '@/modules/club/services/get-club-catalog.usecase';
import { GetClubStatementUseCase } from '@/modules/club/services/get-club-statement.usecase';

// Atendimento (PRAT-64)
import { atendimentoClient } from '@/modules/atendimento/http/atendimento-client';
import { TrocarCodigoOperadorUseCase } from '@/modules/atendimento/services/trocar-codigo-operador.usecase';
import { GetAtendimentoConfigUseCase } from '@/modules/atendimento/services/get-atendimento-config.usecase';
import { ListAtendimentoClientesUseCase } from '@/modules/atendimento/services/list-atendimento-clientes.usecase';
import { BuscarClienteTelefoneUseCase } from '@/modules/atendimento/services/buscar-cliente-telefone.usecase';
import { SalvarAtendimentoClienteUseCase } from '@/modules/atendimento/services/salvar-atendimento-cliente.usecase';
import { CriarPedidoAtendimentoUseCase } from '@/modules/atendimento/services/criar-pedido-atendimento.usecase';
import { FinalizarPedidoAtendimentoUseCase } from '@/modules/atendimento/services/finalizar-pedido-atendimento.usecase';
import { ListPedidosAtendimentoUseCase } from '@/modules/atendimento/services/list-pedidos-atendimento.usecase';
import { GetPedidoAtendimentoUseCase } from '@/modules/atendimento/services/get-pedido-atendimento.usecase';

type ServiceProviderProps = {
  children: ReactNode;
};

export function ServiceProvider({ children }: ServiceProviderProps) {
  const db = useMemo(() => {
    const database = AppDB.getInstance({
      items: 'id',
    });
    return database;
  }, []);

  const httpClient = useMemo(() => {
    return HttpKyClient.create();
  }, []);

  // Atendimento (PRAT-64): manda o token do operador nas rotas da loja dele.
  const atendimentoHttpClient = useMemo(() => HttpKyClient.fromKy(atendimentoClient), []);

  if (!httpClient) throw new Error('HttpClient não inicializado.');

  if (!db) throw new Error('DB não inicializado.');

  const services = useMemo(() => {
    // Instâncias base
    const getPlaceUseCase = new GetPlaceUseCase(httpClient);

    const serviceInstances = {
      // Stores
      getStores: new GetStoresUseCase(httpClient),
      searchMerchants: new SearchMerchantsUseCase(httpClient),
      getStoreItems: new GetStoreItemsUseCase(httpClient),
      getMerchantDetails: new GetMerchantDetailsUseCase(httpClient),
      getStoreReviews: new GetStoreReviewsUseCase(httpClient),

      // Orders
      listOrders: new ListOrdersUseCase(httpClient),
      getDeliveryTax: new GetDeliveryTaxUseCase(httpClient),
      makeOrder: new MakeOrderUsecase(httpClient),
      getOrder: new GetOrderUseCase(httpClient),
      changePaymentMethod: new ChangePaymentMethodUseCase(httpClient),
      redeemCupom: new RedeemCupomUseCase(httpClient),
      listAvailableCoupons: new ListAvailableCouponsUseCase(httpClient),
      reorder: new ReorderUseCase(db, httpClient),
      checkPendingOrders: new CheckPendingOrdersService(httpClient),
      getAvailableSchedule: new GetAvailableScheduleUseCase(httpClient),
      getOrderStatusHistory: new GetOrderStatusHistoryUseCase(httpClient),
      rateOrder: new RateOrderUseCase(httpClient),
      getOrderChatToken: new GetOrderChatTokenUseCase(httpClient),
      verifyPayment: new VerifyPaymentUseCase(httpClient),

      // Location
      getPlace: getPlaceUseCase,
      addUserAddress: new AddUserAddressUseCase(httpClient),
      editUserAddress: new EditUserAddressUseCase(httpClient),
      deleteUserAddress: new DeleteUserAddressUseCase(httpClient),
      listUserAddresses: new ListUserAddressesUseCase(httpClient),
      getSelectedAddress: new GetSelectedAddressUseCase(),
      setSelectedAddress: new SetSelectedAddressUseCase(),
      convertCoordinatesToAddress: new ConvertCoordinatesToAddressUseCase(getPlaceUseCase),
      lookupCep: new LookupCepUseCase(httpClient),

      // Cart
      listItems: new ListItemsUseCase(db),
      getItem: new GetItemUseCase(db),
      editItem: new EditItemUseCase(db),
      addItem: new AddItemUseCase(db),
      removeItem: new RemoveItemUseCase(db),
      incrementQuantity: new IncrementQuantityUseCase(db),
      decrementQuantity: new DecrementQuantityUseCase(db),
      clearCart: new ClearCartUseCase(db),
      checkCartAvailability: new CheckCartAvailabilityUseCase(db, httpClient),

      // Payment
      getPaymentWays: new GetPaymentWaysUseCase(httpClient),

      // Account
      getProfile: new GetProfileUseCase(httpClient),
      updateProfile: new UpdateProfileUseCase(httpClient),
      // Perfil completo: e-mail confirmado e CPF (PRAT-161)
      getProfileCompletion: new GetProfileCompletionUseCase(httpClient),
      requestEmailConfirmation: new RequestEmailConfirmationUseCase(httpClient),

      // Clube pratiGO (PRAT-27)
      getClubSummary: new GetClubSummaryUseCase(httpClient),
      getClubCatalog: new GetClubCatalogUseCase(httpClient),
      getClubStatement: new GetClubStatementUseCase(httpClient),

      // Atendimento (PRAT-64)
      trocarCodigoOperador: new TrocarCodigoOperadorUseCase(atendimentoHttpClient),
      getAtendimentoConfig: new GetAtendimentoConfigUseCase(atendimentoHttpClient),
      listAtendimentoClientes: new ListAtendimentoClientesUseCase(atendimentoHttpClient),
      buscarClienteTelefone: new BuscarClienteTelefoneUseCase(atendimentoHttpClient),
      salvarAtendimentoCliente: new SalvarAtendimentoClienteUseCase(atendimentoHttpClient),
      criarPedidoAtendimento: new CriarPedidoAtendimentoUseCase(atendimentoHttpClient),
      finalizarPedidoAtendimento: new FinalizarPedidoAtendimentoUseCase(atendimentoHttpClient),
      listPedidosAtendimento: new ListPedidosAtendimentoUseCase(atendimentoHttpClient),
      getPedidoAtendimento: new GetPedidoAtendimentoUseCase(atendimentoHttpClient),
    };

    return serviceInstances;
  }, [db, httpClient, atendimentoHttpClient]);

  // Memoiza o contexto para evitar re-renderizações
  const contextValue = useMemo(() => services, [services]);

  return <ServiceContext value={contextValue}>{children}</ServiceContext>;
}
